import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Landmark, ArrowRight, Instagram, Facebook, Linkedin, Phone } from 'lucide-react';
import content from '../data/content.json';

const Navbar = ({ data }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [hoveredLink, setHoveredLink] = useState(null);

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const navLinks = data.links;

    return (
        <>
            <motion.nav
                initial={false}
                animate={{
                    paddingTop: isScrolled ? '0.5rem' : '1rem',
                    paddingBottom: isScrolled ? '0.5rem' : '1rem',
                    backgroundColor: isScrolled ? 'rgba(255, 255, 255, 0.98)' : 'rgba(255, 255, 255, 0)',
                    backdropFilter: isScrolled ? 'blur(20px)' : 'blur(0px)',
                    boxShadow: isScrolled ? '0 4px 30px rgba(0, 0, 0, 0.03)' : 'none',
                    y: 0
                }}
                transition={{
                    type: "tween",
                    ease: [0.23, 1, 0.32, 1], // Custom power ease for buttery smoothness
                    duration: 0.5
                }}
                className="fixed top-0 left-0 w-full z-[100] border-b border-transparent data-[scrolled=true]:border-slate-200/50"
                data-scrolled={isScrolled}
            >
                <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12 flex justify-between items-center relative md:h-16 h-12">

                    {/* Brand Identity - Precision Aligned */}
                    <a href="/" aria-label="Go to homepage" className="flex items-center gap-3 md:gap-4 group cursor-pointer relative z-10 shrink-0 h-full">
                        <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className={`md:w-11 md:h-11 w-9 h-9 rounded-lg md:rounded-xl flex items-center justify-center transition-all duration-500 shadow-2xl ${isScrolled ? 'bg-primary text-white' : 'bg-white text-primary'
                                }`}
                        >
                            <Landmark className="md:size-[22px] size-5" />
                        </motion.div>
                        <div className="flex flex-col justify-center">
                            <h1 className={`text-base md:text-xl font-black uppercase tracking-tighter transition-colors duration-500 leading-none ${isScrolled ? 'text-primary' : 'text-white'
                                }`}>
                                {data.brand?.first || 'Sarah'} <span className="text-accent">{data.brand?.last || 'Mitchell'}</span>
                            </h1>
                            <div className={`flex items-center gap-1.5 md:gap-2 text-[0.45rem] md:text-[0.55rem] font-black uppercase tracking-[0.2em] md:tracking-[0.25em] transition-opacity duration-500 mt-1 md:mt-1.5 ${isScrolled ? 'text-primary/40' : 'text-white/60'
                                }`}>
                                <span className="w-1 h-1 rounded-full bg-accent" />
                                {data.brand?.company || 'Hartford Elite Realty'}
                            </div>
                        </div>
                    </a>

                    {/* Desktop Navigation - Glass Readability Shield */}
                    <div className="hidden lg:flex items-center relative h-full">
                        <motion.div
                            initial={false}
                            animate={{
                                backgroundColor: isScrolled ? 'rgba(0,0,0,0)' : 'rgba(255,255,255,0.08)',
                                backdropFilter: isScrolled ? 'blur(0px)' : 'blur(8px)',
                                borderColor: isScrolled ? 'rgba(0,0,0,0)' : 'rgba(255,255,255,0.1)'
                            }}
                            className="flex items-center gap-1 p-1 rounded-2xl border transition-all duration-700"
                        >
                            {navLinks.map((link, i) => (
                                <a
                                    key={i}
                                    href={link.href}
                                    onMouseEnter={() => setHoveredLink(i)}
                                    onMouseLeave={() => setHoveredLink(null)}
                                    className={`relative px-5 py-2 text-[0.65rem] font-black uppercase tracking-[0.2em] transition-colors duration-300 z-10 ${hoveredLink === i
                                        ? (isScrolled ? 'text-white' : 'text-primary')
                                        : (isScrolled ? 'text-primary' : 'text-white')
                                        }`}
                                >
                                    <span className="relative z-20 transition-transform duration-300 block">{link.label}</span>
                                    {hoveredLink === i && (
                                        <motion.div
                                            layoutId="nav-bg"
                                            className={`absolute inset-0 rounded-xl z-10 shadow-sm ${isScrolled ? 'bg-primary' : 'bg-white'
                                                }`}
                                            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                                        />
                                    )}
                                </a>
                            ))}
                        </motion.div>
                    </div>

                    {/* Schedule Call Button - Precision Aligned */}
                    <div className="hidden lg:flex items-center shrink-0 h-full">
                        <button
                            onClick={() => document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' })}
                            aria-label="Schedule a consultation call"
                            className={`btn-premium  group relative px-7 py-3 rounded-xl text-[0.65rem] font-black uppercase tracking-[0.2em] shadow-xl transition-all duration-500 overflow-hidden flex items-center ${isScrolled
                                ? 'bg-primary text-white shadow-black/10'
                                : 'bg-white text-primary shadow-white/10'
                                } hover:text-white`}>
                            <div className="absolute inset-0 bg-accent translate-x-[-101%] group-hover:translate-x-0 transition-transform duration-700 ease-[cubic-bezier(0.19,1,0.22,1)]" />
                            <span className="relative z-10 flex items-center gap-2">
                                {data.cta}
                                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform duration-300" />
                            </span>
                        </button>
                    </div>

                    <div className="lg:hidden flex items-center h-full">
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            aria-label="Toggle navigation menu"
                            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 active:scale-90 ${isScrolled ? 'bg-slate-50 text-primary' : 'bg-white/10 text-white backdrop-blur-md'
                                }`}
                        >
                            {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>

                </div>
            </motion.nav>

            {/* FULL SCREEN MOBILE MENU */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, x: '100%' }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: '100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed inset-0 z-[110] bg-white flex flex-col lg:hidden"
                    >
                        <div className="flex justify-between items-center p-8">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-primary text-white rounded-xl flex items-center justify-center shadow-lg">
                                    <Landmark size={22} />
                                </div>
                                <span className="text-xl font-black text-primary uppercase">{data.brand?.first} {data.brand?.last}</span>
                            </div>
                            <button
                                onClick={() => setIsMenuOpen(false)}
                                aria-label="Close navigation menu"
                                className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-primary"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="flex-grow flex flex-col justify-center px-8 gap-1">
                            {navLinks.map((link, i) => (
                                <motion.a
                                    key={i}
                                    href={link.href}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    onClick={() => setIsMenuOpen(false)}
                                    className="group flex items-center justify-between p-4 sm:p-8 rounded-[32px] hover:bg-slate-50 transition-colors"
                                >
                                    <span className="text-2xl sm:text-4xl font-black text-primary uppercase tracking-tighter">
                                        {link.label}
                                    </span>
                                    <div className="w-12 h-12 rounded-full border border-slate-200 flex items-center justify-center text-slate-300 group-hover:bg-accent group-hover:text-white group-hover:border-accent transition-all">
                                        <ArrowRight size={24} />
                                    </div>
                                </motion.a>
                            ))}
                        </div>

                        <div className="p-8 bg-slate-50 flex flex-col gap-6">
                            <button
                                onClick={() => {
                                    setIsMenuOpen(false);
                                    document.getElementById('consultation')?.scrollIntoView({ behavior: 'smooth' });
                                }}
                                className="btn-premium w-full bg-accent text-white py-3 rounded-3xl font-black uppercase tracking-widest text-lg shadow-xl shadow-accent/20"
                            >
                                {data.cta}
                            </button>
                            <div className="flex justify-center gap-8">
                                {[
                                    { Icon: Instagram, href: content.contact?.socials?.instagram, label: "Instagram" },
                                    { Icon: Facebook, href: content.contact?.socials?.facebook, label: "Facebook" },
                                    { Icon: Linkedin, href: content.contact?.socials?.linkedin, label: "LinkedIn" }
                                ].map(({ Icon, href, label }, i) => (
                                    <a key={i} href={href} aria-label={label} target="_blank" rel="noopener noreferrer" className="text-primary/40 hover:text-accent transition-colors"><Icon size={20} /></a>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;
